import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useState,
} from "react";
import axios from "axios";
import Image from "next/image";
import Modal, { ModalProps } from "./Modal";
import AsyncSelect from "react-select/async";
import { debounce } from "lodash";
import { selectStyles } from "@/styles/select";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  useController,
  useForm,
} from "react-hook-form";
import { GAMEMODES, RANK, ROLES } from "@/constants";
import { Teamfinders } from "@/models/teamfinder.model";

type Props = ModalProps & {
  setTeamfinderAgents: Dispatch<SetStateAction<Teamfinders>>;
};

const TeamfinderFilterModal = (props: Props) => {
  const { isModalOpen, setIsModalOpen, setTeamfinderAgents } = props;
  const { control, handleSubmit } = useForm();

  const [cityQuery, setCityQuery] = useState("");
  const [schoolQuery, setSchoolQuery] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadCityOptions = useCallback(
    debounce((inputText, callback) => {
      axios
        .post(`/api/find-city`, { name: inputText })
        .then((options) => callback(options.data));
    }, 500),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadSchoolOptions = useCallback(
    debounce((inputText, callback) => {
      axios
        .post(`/api/find-school`, { name: inputText })
        .then((options) => callback(options.data));
    }, 500),
    []
  );

  const onSubmitFilters = handleSubmit((data) => {
    const ranks = (data.ranks || [])
      .map((rank: string) => rank?.toUpperCase())
      .filter((rank: any) => rank);

    const roles = (data.roles || [])
      .map((role: string) => role?.toUpperCase())
      .filter((role: any) => role);

    const modes = (data.modes || []).filter((mode: any) => mode);

    const location = data.city?.value;
    const school = data.school?.value;

    const payload = { ranks, roles, modes, location, school };

    axios.post(`/api/teamfinders`, payload).then((res) => {
      setTeamfinderAgents(res.data);
      setIsModalOpen(false);
    });
  });

  return (
    <Modal
      title="Filter teamfinder agents"
      isOpen={isModalOpen}
      closeModal={() => setIsModalOpen(false)}
    >
      <form
        className="mt-4 flex flex-col justify-between"
        onSubmit={onSubmitFilters}
      >
        <div className="grid grid-cols-[2fr_1fr] gap-20">
          <div className="space-y-16">
            <FilterGroup
              type="rank"
              name="Summoner rank"
              filterOptions={RANK}
              control={control}
              registerAs="ranks"
            />
            <FilterGroup
              type="role"
              name="Summoner role"
              filterOptions={ROLES}
              control={control}
              registerAs="roles"
            />
            <FilterGroup
              type="mode"
              name="Game modes"
              filterOptions={GAMEMODES}
              control={control}
              registerAs="modes"
            />
          </div>
          <div className="flex flex-col">
            <div className="flex-1">
              <p className="mb-3 text-sm font-medium text-slate-300">
                Location
              </p>
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <AsyncSelect
                    {...field}
                    placeholder="Search city"
                    inputValue={cityQuery}
                    onInputChange={setCityQuery}
                    loadOptions={loadCityOptions}
                    defaultValue={[]}
                    defaultOptions={[]}
                    styles={selectStyles}
                    isClearable
                    isSearchable
                    className="react-select-input"
                  />
                )}
              />
            </div>
            <div className="flex-1">
              <p className="mb-3 text-sm font-medium text-slate-300">School</p>
              <Controller
                name="school"
                control={control}
                render={({ field }) => (
                  <AsyncSelect
                    {...field}
                    placeholder="Search school"
                    inputValue={schoolQuery}
                    onInputChange={setSchoolQuery}
                    loadOptions={loadSchoolOptions}
                    defaultValue={[]}
                    defaultOptions={[]}
                    styles={selectStyles}
                    isClearable
                    isSearchable
                    className="react-select-input"
                  />
                )}
              />
            </div>
          </div>
        </div>
        <div className="-m-4 mt-8 space-x-4 bg-slate-900/40 py-4 px-6 md:-m-6 md:mt-12">
          <button
            type="button"
            className="rounded border border-slate-700 px-4 py-2 text-sm font-medium text-slate-400"
            onClick={() => setIsModalOpen(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="rounded-md bg-blue-200 py-2 px-4 text-sm font-medium text-blue-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Save changes
          </button>
        </div>
      </form>
    </Modal>
  );
};

type FilterGroupProps = {
  name: string;
  type: "rank" | "role" | "mode";
  filterOptions: string[];
  registerAs: Path<FieldValues>;
  control: Control;
};

const FilterGroup = (props: FilterGroupProps) => {
  const { name, type, filterOptions, registerAs, control } = props;
  const { field } = useController({ control, name: registerAs });

  const [value, setValue] = useState(field.value || []);

  function handleChane(e: ChangeEvent<HTMLInputElement>, index: number) {
    const valueCopy = [...value];

    // update checkbox value
    e.target.checked === false
      ? (valueCopy[index] = null)
      : (valueCopy[index] = e.target.value);

    // send data to react hook form
    field.onChange(valueCopy);

    // update local state
    setValue(valueCopy);
  }

  return (
    <div>
      <p className="mb-3 border-b border-slate-700/70 pb-3 text-sm font-medium text-slate-300">
        {name}
      </p>
      <div className="grid gap-x-12 gap-y-4 lg:grid-cols-3">
        {filterOptions.map((item, index) => (
          <div key={item} className="flex items-center justify-between gap-8">
            <div className="flex items-center space-x-2">
              {type === "rank" && (
                <Image
                  src={`/images/emblems/${item}_Emblem.webp`}
                  width={35}
                  height={35}
                  alt=""
                />
              )}
              {type === "role" && (
                <Image
                  src={`/images/role_icons/${item}_icon.png`}
                  width={24}
                  height={24}
                  alt=""
                />
              )}
              <label
                className="text-sm font-normal"
                htmlFor={item.toUpperCase()}
              >
                {item}
              </label>
            </div>
            <input
              id={item.toUpperCase()}
              type="checkbox"
              value={item}
              checked={value.includes(item)}
              onChange={(e) => handleChane(e, index)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default TeamfinderFilterModal;
